// KidModeShell — root component for the kid experience. Mounted from
// App.jsx whenever the signed-in profile has profile_role === 'child'.
// Takes over the entire viewport; no adult chrome leaks through.
//
// EMOJI-IN-UI EXCEPTION: this tree (src/experiments/kids-mode/**) is
// the explicit opt-in to emojis per the plan. No-emoji rule still
// holds elsewhere in the app.
//
// M1 scope: Pet tab only is wired. Cards / Watch / Craft tabs render
// placeholder "coming soon" panels until M2-M4 land.

import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { COLOR, FONT } from "../../lib/tokens";
import { useKidPetState } from "../../lib/useKidPetState";
import { useKidStepNarration } from "../../lib/useKidStepNarration";
import { useKidCardGrants } from "../../lib/useKidCardGrants";
import { Pet } from "./Pet";
import KidCardDeck from "./KidCardDeck";
import PetStatsPanel from "./PetStatsPanel";
import PetArButton from "./PetArButton";
import BattleSheet from "./BattleSheet";
import KidCookBanner from "./KidCookBanner";
import CardRevealOverlay from "./CardRevealOverlay";
import { KidsTabBar } from "./primitives";
import { PinPad } from "./PinPad";
import { KidParentPanel } from "./KidParentPanel";
import { FoodExplorer } from "./FoodExplorer";
import { KidRatePrompt } from "./KidRatePrompt";
import { Craft } from "./Craft";
import { KidWatchTab } from "./KidWatchTab";
import { ColoringBook } from "./ColoringBook";
import { useTeamLiveActivity } from "../../lib/useTeamLiveActivity";
import { useUserRecipes } from "../../lib/useUserRecipes";

const TABS = [
  { id: "pet",   emoji: "🐾", label: "Pet"   },
  { id: "foods", emoji: "🍎", label: "Foods" },
  { id: "craft", emoji: "🧪", label: "Mix"   },
  { id: "watch", emoji: "👀", label: "Watch" },
];

export function KidModeShell({ kidId, userId: legacyUserId, onExit }) {
  // Accept `kidId` (the new context-switch prop from App.jsx) OR
  // fall back to `userId` for any legacy callsite. Both are the
  // same UUID semantically — profile.id.
  const userId = kidId || legacyUserId;
  const [active, setActive] = useState("pet");
  const [pinModal, setPinModal] = useState(false);
  const [parentPanel, setParentPanel] = useState(false);

  // Team Live Activity mirror — when a parent (or sibling) is
  // cooking, the kid's iPad lock screen shows the same timer
  // widget. App.jsx skips this hook for child profiles (returns
  // early into the kid shell), so it has to live HERE for kids
  // to ever get the Dynamic Island / Lock Screen card.
  const { recipes: kidRecipes } = useUserRecipes(userId) || {};
  const findUserRecipeBySlug = (slug) =>
    (kidRecipes || []).find((r) => r.slug === slug)?.recipe || null;
  useTeamLiveActivity(userId, { findUserRecipe: findUserRecipeBySlug });

  // Ambient pet-voice narration of cook step transitions for any
  // food the kid asked for. Mounted at shell-level so the kid hears
  // "Your egg burger is in the oven!" while coloring, on the Pet
  // tab, exploring Foods — anywhere in kids mode.
  useKidStepNarration(userId);
  const [hasPin, setHasPin] = useState(false);
  const { pet, mood, activeStats, loading } = useKidPetState(userId);

  // Pending card-grant queue. The oldest unrevealed grant pops the
  // full-screen CardRevealOverlay (flash → spin → reveal → collect).
  // Mounted above the parent panel + PIN modal so it ALWAYS surfaces
  // on first paint, no matter which tab the kid was last on.
  const { pendingGrants, claim } = useKidCardGrants(userId);
  const headGrant = pendingGrants[0] || null;

  // Probe for an exit PIN on mount. Migration 0226 changed
  // has_kid_exit_pin to take an explicit p_user_id; for kid-mode
  // we pass the active kid's profile.id.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userId) return;
      const { data, error } = await supabase.rpc("has_kid_exit_pin", { p_user_id: userId });
      if (cancelled) return;
      if (error) {
        console.warn("[kid-mode] has_kid_exit_pin:", error.message);
        setHasPin(false);
        return;
      }
      setHasPin(data === true);
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Gear tap: gated by PIN if one is set, otherwise opens the
  // parent panel directly. The panel itself surfaces Exit Kids
  // Mode, change/remove PIN, pause hunger — everything a grown-up
  // needs without having to sign out first.
  const handleGearTap = () => {
    if (hasPin) {
      setPinModal(true);
    } else {
      setParentPanel(true);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "linear-gradient(180deg, #FFF8E7 0%, #FAF6EE 60%, #F2EBD9 100%)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Top-right corner: small sign-out tile. Kid taps to switch
          accounts (parent walks them through new magic link). No
          text label — universally recognizable door icon. */}
      <button
        type="button"
        onClick={handleGearTap}
        aria-label="grown-up settings"
        className="mcm-focusable"
        style={{
          position: "absolute",
          top: "max(16px, env(safe-area-inset-top))",
          right: "max(16px, env(safe-area-inset-right))",
          width: 48,
          height: 48,
          fontSize: 24,
          background: "rgba(255,255,255,0.6)",
          border: `1px solid ${COLOR.border}`,
          borderRadius: 12,
          cursor: "pointer",
          touchAction: "manipulation",
          zIndex: 50,
        }}
      >
        ⚙️
      </button>

      {/* Scroll viewport. Outer shell sets overflow:hidden so the
          tab bar stays pinned; this inner box owns the scroll. Inner
          wrapper carries min-height:100% so the centered pet/watch
          layouts still center when content fits, but grow + allow
          scroll past the tab bar when the coloring-book tiles (or
          any future below-the-pet content) overflow the viewport. */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          position: "relative",
          width: "100%",
        }}
      >
       <div
        style={{
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: (active === "foods" || active === "craft") ? "stretch" : "center",
          justifyContent: (active === "foods" || active === "craft") ? "flex-start" : "center",
          padding: (active === "foods" || active === "craft")
            ? "max(72px, calc(env(safe-area-inset-top) + 24px)) 0 140px"
            : "max(48px, env(safe-area-inset-top)) 16px 140px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {active === "pet" && (
          <>
            {/* Live-cook banner — surfaces whenever ANY family member
                has an active cook session. Tap → jumps to the Watch
                tab so the kid lands on the live cook view. Renders
                nothing when nobody's cooking. */}
            <KidCookBanner userId={userId} onTapToWatch={() => setActive("watch")} />
            <PetTab pet={pet} mood={mood} loading={loading} userId={userId} activeStats={activeStats} />
            {/* Coloring book — every line-art page the kid generated
                via "🎨 COLOR THIS" lives here. Tap any tile to
                reopen the canvas (resumes from the saved colored
                composite when present). Persisted artifacts; never
                lost just because the canvas was closed. */}
            <div style={{ marginTop: 28, width: "100%", maxWidth: 480 }}>
              <ColoringBook kidId={userId} />
            </div>
          </>
        )}
        {active === "foods" && <FoodExplorer userId={userId} onWatch={() => setActive("watch")} />}
        {active === "craft" && <Craft userId={userId} />}
        {active === "watch" && <KidWatchTab userId={userId} />}
       </div>
      </div>

      <KidsTabBar active={active} onChange={setActive} tabs={TABS} />

      {pinModal && (
        <PinPad
          kidId={userId}
          onCancel={() => setPinModal(false)}
          onSuccess={() => {
            setPinModal(false);
            setParentPanel(true);
          }}
        />
      )}

      {parentPanel && (
        <KidParentPanel
          userId={userId}
          pet={pet}
          onClose={() => setParentPanel(false)}
          onExit={onExit}
        />
      )}

      {headGrant && (
        <CardRevealOverlay
          key={headGrant.id}
          grant={headGrant}
          onCollect={() => claim(headGrant.id)}
        />
      )}
    </div>
  );
}

function PetTab({ pet, mood, loading, userId, activeStats }) {
  const [battleOpen, setBattleOpen] = useState(false);
  if (loading && !pet) {
    return (
      <div style={{ fontSize: 80 }}>
        <span style={{ display: "inline-block", animation: "kid-spin 1.6s linear infinite" }}>🥚</span>
        <style>{`@keyframes kid-spin { 0% { transform: rotate(0); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", padding: "0 16px" }}>
      {/* Rate-a-meal banner — only renders when there's a pending
          cook this kid was tagged on. Self-hides on empty state. */}
      {userId && <KidRatePrompt userId={userId} />}
      <Pet pet={pet} mood={mood} />
      {pet?.pet_name && (
        <div
          style={{
            marginTop: 24,
            fontFamily: FONT.itemTitle,
            fontStyle: "italic",
            fontSize: 24,
            color: COLOR.ink,
            opacity: 0.85,
          }}
        >
          {pet.pet_name}
        </div>
      )}
      {pet?.level >= 1 && (
        <div
          style={{
            marginTop: 14,
            width: 220,
            maxWidth: "70vw",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              fontFamily: FONT.mono,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.08em",
              color: COLOR.ink,
              marginBottom: 4,
            }}
          >
            <span>LV. {pet.level}</span>
            <span style={{ color: COLOR.muted, fontSize: 10 }}>
              {pet.xp_in_level || 0} / 100 XP
            </span>
          </div>
          <div
            style={{
              width: "100%",
              height: 8,
              background: "rgba(0,0,0,0.08)",
              borderRadius: 999,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${Math.min(100, ((pet.xp_in_level || 0) / 100) * 100)}%`,
                height: "100%",
                background: `linear-gradient(90deg, ${COLOR.leaf}, ${COLOR.gold})`,
                transition: "width 600ms cubic-bezier(0.34, 1.56, 0.64, 1)",
              }}
            />
          </div>
        </div>
      )}
      {/* AR Quick Look — iOS only. Renders nothing off-iOS so the
          kid doesn't see a dead button on web/Android. Tap puts the
          alien idle USDZ in the room via the device camera. Pass
          userId + pet so card-chip taps in AR can spend cards
          against the kid's actual balance. */}
      <PetArButton userId={userId} pet={pet} />

      {/* ⚔️ BATTLE — opens the PvE fight sheet. Every attack inside
          spends a card via spend_kid_card, so the parent-gated
          eating economy stays the only source of growth. */}
      <div style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
        <button
          type="button"
          onClick={() => setBattleOpen(true)}
          className="mcm-focusable"
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "12px 22px",
            background: "linear-gradient(135deg, #E84A5F 0%, #B71540 100%)",
            color: "#fff", border: "none", borderRadius: 999,
            fontFamily: FONT.mono, fontSize: 13, fontWeight: 800,
            letterSpacing: "0.12em", cursor: "pointer",
            boxShadow: "0 6px 18px rgba(232,74,95,0.40)",
            touchAction: "manipulation",
          }}
        >
          <span style={{ fontSize: 16, lineHeight: 1 }}>⚔️</span>
          <span>BATTLE</span>
        </button>
      </div>
      {battleOpen && (
        <BattleSheet userId={userId} onClose={() => setBattleOpen(false)} />
      )}

      {/* Pet-feeding card deck. Counts come from pet.cards_*
          (migration 0238). Tap a card → spend_kid_card RPC → count
          decrements + matching has_X boolean flips ON (powering the
          pet's art axis). */}
      {pet && <KidCardDeck pet={pet} userId={userId} />}

      {/* Fighter stats — time-windowed (migration 0264). Each card
          spent = +1 to its matching stat for 3 hours. activeStats
          is recomputed every 60s by useKidPetState so the panel
          shows the decay in real time. */}
      {pet && <PetStatsPanel pet={pet} activeStats={activeStats} />}
    </div>
  );
}

function PlaceholderTab({ emoji, hint }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 120, lineHeight: 1, opacity: 0.45 }}>{emoji}</div>
      <div
        style={{
          marginTop: 16,
          fontFamily: FONT.forma,
          fontSize: 14,
          color: COLOR.muted,
          maxWidth: 280,
          lineHeight: 1.4,
        }}
      >
        {hint}
      </div>
    </div>
  );
}

export default KidModeShell;
